// @ts-nocheck
import { useState, useEffect } from "react";
import { useUserType } from "../../../../../hooks/useUserType";
import { XcaliberModelButton } from "../../../../../components/buttons/Buttons";
import cornerstone from "cornerstone-core";
import cornerstoneTools from "cornerstone-tools";
import { 
    XCALIBER_ANALYSIS_MODELS, 
    TOOL_IDS, 
    XCALIBER_NON_SLIDER_RESULT_TYPES,
    XCALIBER_NON_SLIDER_MODELS 
} from "../../consts/tools.consts";
import { useXcaliberAnalysis } from "pages/viewer/hooks/xcaliber/useXcaliberAnalysis";
import { toast } from "react-toastify"; 
import useSelectedXcaliberModels from "pages/viewer/hooks/xcaliber/useSelectedXcaliberModels";
import { addXcaliberPdfNotes, retrieveXcaliberPdf, submitXCaliberRequest } from "services/xCaliber";
import { useAppContext } from "context/app.context";
import { ToolSpinner } from "pages/viewer/components/dicom-viewer-toolbar/tool-button/ToolSpinner";
import { useViewerContext } from "pages/viewer/context/viewer.context";

export const XcaliberPdfButton = (  {selectedModels, setSelectedModels}  ) => {
    const { userTypeColour } = useUserType();
    const [availableModels, setAvailableModels] = useState([]);
    const xcaliberAnalysis = useXcaliberAnalysis();
    const { state: { userId, xcaliberStatus } } = useAppContext();
    const [isRetrievingPdf, setIsRetrievingPdf] = useState(false);
    const [isSubmittingNotes, setIsSubmittingNotes] = useState(false);
    const { state: { xcaliberAnalysisStudy, activeToolIds } } = useViewerContext();
    const [showModal, setShowModal] = useState(false);
    const [inputValues, setInputValues] = useState(() => {
        // Initialize one entry per model
        const init = {};
        availableModels.forEach(m => init[m] = '');
        return init;
    });
    // Ensure initial state is set once models are available
    const [selectedModel, setSelectedModel] = useState(null);

    useEffect(() => {
        if (availableModels.length > 0) {
        setSelectedModel(availableModels[0]);
        setInputValues(() =>
            availableModels.reduce((acc, model) => {
            acc[model] = ''; // each gets its own empty string
            return acc;
            }, {})
        );
        }
    }, [availableModels]);

    const modalStyle = {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    };

    useEffect(() => {
        if (
            xcaliberAnalysis.xcaliberAnalysis != null &&
            xcaliberAnalysis.xcaliberAnalysis?.data
        ) {
            const availableModels = XCALIBER_ANALYSIS_MODELS.filter(
                (key) => key in xcaliberAnalysis.xcaliberAnalysis.data
            );
            setAvailableModels(availableModels);
        }
    }, [xcaliberAnalysis]);

    const generatePdf = async () => {
        if(isRetrievingPdf) {
            return;
        }
        if(!xcaliberAnalysis || !xcaliberAnalysis.xcaliberAnalysis || !xcaliberAnalysis.xcaliberAnalysis.data) {
            //TODO: FIRST CHECK THE OTHER STUDIES FOR ANALYSES AND IF THEY EXIST THEN PRODUCE PDF FOR THEM. ONLY IF NONE EXIST THEN PRODUCE THE ERROR TOAST.
            toast.error("Cannot retrieve Pdf. No analysis available.");
            return;
        }

        setIsRetrievingPdf(true);
        const data = xcaliberAnalysis.xcaliberAnalysis.data;
        const thresholdSliderElement = document.getElementById("xcaliberSlider") as HTMLInputElement;
        let currentThreshold = thresholdSliderElement.value;
        let onePdfSubmissionOnly = true; //legacy submissions required one pdf per model, however now the pdf is model/study-wide
        for (const model of availableModels) {
            if(onePdfSubmissionOnly) {
                onePdfSubmissionOnly = false;
                const resultType = data[model]?.['analysisRslt']?.[0]?.result_type;
                if( XCALIBER_NON_SLIDER_MODELS.includes(model) ) { //if( XCALIBER_NON_SLIDER_RESULT_TYPES.includes(resultType) ) {
                    currentThreshold = 30;
                }
                if(selectedModels.includes(model)) {
                    const reqId = xcaliberAnalysis.xcaliberAnalysis.data[model]['analysisId'];
                    const dtsId = xcaliberAnalysis.xcaliberAnalysis.data[model]['analysisDtsId'];
                    if(!userId) {
                        toast.error("Unable to download analysis report. User ID unknown.");
                        return;
                    }
                    if(!reqId) {
                        toast.error("Unable to download analysis report. Request ID missing.");
                        return;
                    }

                    const inputValuesArray = convertToDrOpinionList(inputValues);
                    await addXcaliberPdfNotes(userId, inputValuesArray);

                    const result = await retrieveXcaliberPdf(userId, reqId, currentThreshold);
                    if (result.status && result.status == 202) {
                        toast.success("Analysis in Progress. Please try again soon.", {
                            position: "top-center",
                            style: { background: this.colourValue, color: "white" },
                        });
                    } else {
                        const url = window.URL.createObjectURL(result);
                        const newWindow = window.open(url, "_blank");
                        if (
                            !newWindow ||
                            newWindow.closed ||
                            typeof newWindow.closed === "undefined"
                        ) {
                            alert(
                            "The PDF could not be opened. Please check your popup blocker settings then retry"
                            );
                        }
                    }
                }
            }
        };
        setIsRetrievingPdf(false);
    };

    const handleClick = async () => {
        if(!isRetrievingPdf) {
            setShowModal(true);
        };
    };

    const handleSubmitNotes = async () => {
        setIsRetrievingPdf(true);
        setIsSubmittingNotes(true);
        const inputValuesArray = convertToDrOpinionList(inputValues);
        await addXcaliberPdfNotes(userId, inputValuesArray);
        setIsRetrievingPdf(false);
        setIsSubmittingNotes(false);
    };
    
    const handleCancel = () => {
        setShowModal(false);
    };

    const handleInputChange = (e) => {
        setInputValues(prev => ({
          ...prev,
          [selectedModel]: e.target.value,
        }));
    };

    const handleDownloadPdf = async () => {
        setShowModal(false);
        const inputValuesArray = convertToDrOpinionList(inputValues);        
        await generatePdf();
    };
    
    const convertToDrOpinionList = (inputValuesObject) => {
        const newArray = Object.keys(inputValuesObject).map( (modelKey) => (
            {
                analysisDtsId: xcaliberAnalysis.xcaliberAnalysis.data[modelKey]?.analysisDtsId || null,
                doctrOpinCtt: inputValuesObject[modelKey],
            }
        ));
        return newArray;
    };

    return (
        <div>
        {/* PDF Button */}
        {activeToolIds.includes(TOOL_IDS.XCALIBER_TOOL) && xcaliberAnalysis && xcaliberAnalysis.xcaliberAnalysis && xcaliberAnalysis.xcaliberAnalysis.data &&
        <XcaliberModelButton
            id='xcaliberPdfButton'
            key='xcaliberPdfButton'
            onClick={() => handleClick()} //currently clicks automatically on initialisation
            buttoncolor={ userTypeColour }
            textcolor={ isRetrievingPdf? userTypeColour : "white" }
            display={ "none" }
        >
        {isRetrievingPdf? <ToolSpinner /> : 'PDF'}
        </XcaliberModelButton>
        }

        {/* Modal for Text Entry */}
        {showModal && (
            <div style={modalStyle}>
                <div style={{ backgroundColor: 'white', padding: 20, borderRadius: 8, width: '600px', maxWidth: '90%' }}>

                    {/* Top row with PDF button */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                        {/* Model selector buttons */}
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {availableModels.map(model => (
                                <button
                                key={model}
                                onClick={() => setSelectedModel(model)}
                                style={{
                                    backgroundColor: selectedModel === model ? userTypeColour : '#eee',
                                    color: selectedModel === model ? 'white' : '#000',
                                    padding: '6px 10px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                                >
                                {model.substring(model.lastIndexOf("_") + 1).toUpperCase()}
                                </button>
                            ))}
                        </div>

                        {/* PDF Download button */}
                        <button
                        onClick={handleDownloadPdf}
                        style={{
                            backgroundColor: userTypeColour,
                            color: 'white',
                            padding: '12px 20px',
                            border: 'none',
                            borderRadius: '6px',
                            outline: '2px solid white',
                            outlineOffset: '-4px',
                            cursor: 'pointer',
                            height: 'fit-content',
                          }}
                        >
                        Create PDF
                        </button>
                    </div>

                    <p style={{ color: userTypeColour }}>
                        Enter Veterinarian Note for: <b>{selectedModel.substring(selectedModel.lastIndexOf("_") + 1).toUpperCase()}</b>
                    </p>

                    <textarea
                        value={inputValues[selectedModel] || ''}
                        onChange={handleInputChange}
                        rows={4}
                        style={{
                        marginBottom: 10,
                        padding: 8,
                        width: '100%',
                        resize: 'vertical',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        }}
                    />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
                            {/*Submit-Notes button*/}
                            <button
                            onClick={handleSubmitNotes}
                            style={{
                                color: 'white',
                                backgroundColor: userTypeColour,
                                padding: '8px 14px',
                                borderRadius: '6px',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                            >
                            {isSubmittingNotes? <ToolSpinner /> : 'Submit Notes'}
                            </button>

                            <span style={{ fontSize: '12px', color: userTypeColour }}>*For this image only. Change image to submit others. </span>
                        </div>
                            
                        {/*Cancel button*/}
                        <button
                        onClick={handleCancel}
                        style={{
                            marginLeft: 10,
                            backgroundColor: '#ccc',
                            padding: '8px 14px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                        >
                        Close
                        </button>
                    </div>
                </div>
            </div>
        )}
        </div>
    );
};
